/**
 * Push subscription manager.
 *
 * Negotiates a VAPID Web Push subscription with the backend after the user
 * grants notification permission. Idempotent — safe to call repeatedly on
 * focus / app reopen. Resubscribes automatically if VAPID key rotates.
 *
 * V9.4 — restored from V8.6 architecture for reliable closed-app reminders.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function urlB64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

async function getReg() {
  if (!("serviceWorker" in navigator)) return null;
  return await navigator.serviceWorker.ready;
}

export async function getPushState() {
  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window;
  if (!supported) return { supported: false, subscribed: false };
  const reg = await getReg();
  if (!reg) return { supported: true, subscribed: false };
  const sub = await reg.pushManager.getSubscription();
  return { supported: true, subscribed: !!sub, endpoint: sub?.endpoint };
}

export async function ensurePushSubscribed() {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission !== "granted") return "no-permission";
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return "unsupported";
  }
  try {
    const reg = await getReg();
    if (!reg) return "no-sw";

    // Fetch current VAPID public key
    const r = await fetch(`${BACKEND}/api/push/vapid-public-key`);
    const { key, configured } = await r.json();
    if (!configured || !key) return "vapid-not-configured";

    let sub = await reg.pushManager.getSubscription();

    // If we already have a subscription but it was made with an older
    // VAPID key, drop and resubscribe with the current key.
    if (sub) {
      const existingKey = sub.options?.applicationServerKey;
      const currentKeyBytes = urlB64ToUint8Array(key);
      const matches =
        existingKey &&
        new Uint8Array(existingKey).every((b, i) => b === currentKeyBytes[i]);
      if (!matches) {
        try {
          await sub.unsubscribe();
        } catch {}
        sub = null;
      }
    }

    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(key),
      });
    }

    // Register subscription with backend (idempotent upsert)
    const json = sub.toJSON();
    await fetch(`${BACKEND}/api/push/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        user_agent: navigator.userAgent,
      }),
    });

    return "subscribed";
  } catch (e) {
    console.warn("[push] subscribe failed", e);
    return `error: ${e?.message || "unknown"}`;
  }
}
